// Copyright © 2026 ApexRuntime Labs by Shashi (https://github.com/spshah). All rights reserved.

import { Listbox, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { classNames } from '../../utils/helpers';

export function Select({
  value,
  onChange,
  options,
  placeholder = 'Select…',
  className = '',
}) {
  // options: string[] | { label, value }[]
  const normalised = options
    ? Object.entries(options).map(([key, val]) => ({
        label: val,
        value: val,
        key,
      }))
    : [];

  const selected = normalised.find((o) => o.value === value) ?? null;

  return (
    <Listbox value={value} onChange={onChange}>
      <div className={`relative ${className}`}>
        <Listbox.Button className="relative w-full cursor-default rounded-lg border border-gray-200 bg-white py-2 pl-3 pr-9 text-left text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
          <span className="block truncate text-gray-800">
            {selected?.label ?? (
              <span className="text-gray-400">{placeholder}</span>
            )}
          </span>
          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
            <ChevronDown size={15} className="text-gray-400" />
          </span>
        </Listbox.Button>

        <Transition
          as={Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <Listbox.Options className="absolute z-20 mt-1 max-h-56 w-full overflow-auto rounded-xl bg-white py-1 text-sm shadow-lg ring-1 ring-black/5 focus:outline-none">
            {normalised.map((opt) => (
              <Listbox.Option
                key={opt.key}
                value={opt.value}
                className={({ active }) =>
                  classNames(
                    'relative cursor-default select-none py-2 pl-9 pr-3',
                    active ? 'bg-indigo-50 text-indigo-900' : 'text-gray-900',
                  )
                }
              >
                {({ selected }) => (
                  <>
                    <span
                      className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}
                    >
                      {opt.label}
                    </span>
                    {selected && (
                      <span className="absolute inset-y-0 left-0 flex items-center pl-2.5 text-indigo-600">
                        <Check size={14} />
                      </span>
                    )}
                  </>
                )}
              </Listbox.Option>
            ))}
          </Listbox.Options>
        </Transition>
      </div>
    </Listbox>
  );
}
